// Small JSON client for the map editor's endpoints (Inertia handles pages).
import { trackWrite } from './saveStatus'

export class ApiError extends Error {
  status: number
  data: Record<string, unknown>
  constructor(status: number, data: Record<string, unknown>) {
    super(typeof data.message === 'string' ? data.message : `HTTP ${status}`)
    this.status = status
    this.data = data
  }
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

export async function api<T = unknown>(
  path: string,
  { method = 'GET', body, signal }: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  if (method.toUpperCase() === 'GET') return request<T>(path, method, body, signal)
  const finish = trackWrite()
  try {
    const data = await request<T>(path, method, body, signal)
    finish('ok')
    return data
  } catch (error) {
    // An aborted write was abandoned on purpose; a 4xx was refused and said so.
    if (signal?.aborted || (error instanceof ApiError && error.status < 500)) finish('rejected')
    else finish('lost')
    throw error
  }
}

async function request<T>(path: string, method: string, body: unknown, signal: AbortSignal | undefined): Promise<T> {
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData
  const response = await fetch(path, {
    method,
    signal,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
      ...(body && !isForm ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
  })
  if (response.status === 204) return undefined as T
  // An abort while the body is read must reject, not read as an empty body.
  const data = await response.json().catch(() => {
    if (signal?.aborted) throw signal.reason
    return {}
  })
  if (!response.ok) throw new ApiError(response.status, data)
  return data as T
}
